import React, { FC } from "react";
import { Segmented, Tooltip } from "antd";

import { RateUnit, useRateUnit } from "./useRateUnit";

interface RateUnitToggleProps {
  /**
   * The output unit to name in the second option, e.g. "Cum". Falls back to a
   * generic "unit" when the visible rows disagree or nothing is loaded yet.
   */
  uomLabel?: string;
  /**
   * Pass true when NO visible row has an output basis, so switching would show
   * a table of em dashes. A mixed page — some machines with a standard output,
   * some without — must stay ENABLED: that is the normal state during rollout.
   */
  disabled?: boolean;
  /** Shown as the tooltip when disabled. Explain what to do about it. */
  disabledReason?: string;
  size?: "small" | "middle" | "large";
}

/**
 * Flips every cost-head column on the screen between ₹/hr and ₹ per unit of
 * output. All instances share one app-wide preference (useRateUnit), so the
 * choice follows the user from the fleet list to the task drawer to the reports.
 *
 * A segmented control rather than paired columns on purpose: the fleet list
 * already carries ten head columns plus fixed ones, and doubling them would
 * push the table into permanent horizontal scrolling.
 */
const RateUnitToggle: FC<RateUnitToggleProps> = ({
  uomLabel,
  disabled = false,
  disabledReason,
  size = "small",
}) => {
  const [unit, setUnit] = useRateUnit();

  const control = (
    <Segmented
      size={size}
      value={unit}
      disabled={disabled}
      onChange={(next) => setUnit(next as RateUnit)}
      aria-label="Cost basis"
      options={[
        { label: "₹/hr", value: "HOUR" },
        { label: `₹/${uomLabel || "unit"}`, value: "OUTPUT" },
      ]}
    />
  );

  if (disabled && disabledReason) {
    // Tooltip needs a non-disabled wrapper to receive hover events.
    return (
      <Tooltip title={disabledReason}>
        <span style={{ display: "inline-block", cursor: "not-allowed" }}>
          {control}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={
        unit === "HOUR"
          ? "Showing cost per machine-hour"
          : `Showing cost per ${uomLabel || "unit"} of output`
      }
    >
      {control}
    </Tooltip>
  );
};

export default RateUnitToggle;
